/**
 * LLS-569 item 4. Pure helpers behind the Move-In Date field on the current
 * residency address. The wizard binds the date as MM/DD/YYYY (ngx-mask); the
 * Api exchanges a calendar date, `YYYY-MM-DD` (the property is a DateOnly server-side).
 * Nothing here constructs a Date from an ISO string or serializes one with
 * `toISOString()`: `new Date('2021-03-15')` is parsed as UTC and shifts the day west of
 * Greenwich, and `toISOString()` of a local midnight shifts it east. Both directions are
 * pure string reshaping, so the day the borrower typed is the day that is stored.
 */

import { completedMonthsBetween } from './employment-duration.utils';

export interface ResidencyDuration {
  years: number;
  months: number;
}

export type DurationFields = {
  durationYears?: number | null;
  durationMonths?: number | null;
};

const MASKED = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const ISO_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

/** Parses MM/DD/YYYY. Incomplete masks, calendar rollovers and years before 1900 yield null. */
export function parseMaskedDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = MASKED.exec(value);
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  if (!month || !day || !year || year < 1900) return null;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/** Whole years and months from `start` through `now`; a future start is 0/0. */
export function computeDurationThrough(start: Date, now: Date): ResidencyDuration {
  const m = completedMonthsBetween(start, now);
  return m < 0 || isNaN(m) ? { years: 0, months: 0 } : { years: Math.floor(m / 12), months: m % 12 };
}

export function hasDuration(ra: DurationFields): boolean {
  return (ra.durationYears ?? 0) > 0 || (ra.durationMonths ?? 0) > 0;
}

export function sameDuration(ra: DurationFields, d: ResidencyDuration): boolean {
  return ra.durationYears === d.years && ra.durationMonths === d.months;
}

/** Years/Months are derived and read-only while a parseable move-in date is present. */
export function isDurationLocked(ra: { moveInDate?: string | null }): boolean {
  return parseMaskedDate(ra.moveInDate) !== null;
}

/** Api ISO (`2021-03-15T00:00:00`) or bare date → `03/15/2021`. Masked input passes through. */
export function toMaskedDate(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  if (MASKED.test(value)) return value;
  const match = ISO_PREFIX.exec(value);
  if (!match) return undefined;
  return `${match[2]}/${match[3]}/${match[1]}`;
}

/**
 * The `YYYY-MM-DD` to send for a masked or Api-shaped date. A masked value must pass parseMaskedDate
 * (calendar-valid) or it is dropped rather than rolled over; an Api value keeps only its calendar-date
 * prefix (a legacy `T00:00:00.000Z` suffix is cut, never parsed); anything else is dropped. The Api
 * rejects a UTC instant here, so this must never fall back to `toISOString()`.
 */
export function toIsoDateOnly(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  if (MASKED.test(value)) {
    const match = MASKED.exec(value)!;
    return parseMaskedDate(value) ? `${match[3]}-${match[1]}-${match[2]}` : undefined;
  }
  const iso = ISO_PREFIX.exec(value);
  if (iso) {
    return parseMaskedDate(`${iso[2]}/${iso[3]}/${iso[1]}`) ? `${iso[1]}-${iso[2]}-${iso[3]}` : undefined;
  }
  return undefined;
}
